/**
 * Editor Integration — session creation.
 *
 *   native editor document → validate → normalize → resolve → session
 *
 * The result is a document the editor can open with no conversion of its own:
 * pages already in its shape, assets resolved, fonts collected, theme applied,
 * and its history, selection and autosave already initialised.
 *
 * The editor is never modified. This layer prepares the document for editing;
 * it does not edit it, and it does not open it — routing is the caller's job.
 *
 * Pure and deterministic: no clock, no randomness, no I/O.
 */

import {
  SESSION_SCHEMA_ID, SESSION_VERSION, EDITOR_DEFAULTS, AUTOSAVE, HISTORY_LIMIT,
  ANALYTICS_EVENTS, CAPABILITIES, META_NAMESPACE,
} from './sessionSchema';
import { validateEditorCompatibility } from './compatibility';
import { normalizeEditorDocument } from './normalize';
import {
  resolveAssets, resolveFonts, resolveImagePlaceholders, resolveTheme, applyTheme,
  resolveBackgrounds, identityAssetResolver,
} from './resolve';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isStr = (v) => typeof v === 'string' && v.length > 0;

/**
 * A deep, frozen copy — a history snapshot must not move when the document it
 * was taken from is edited. Structural sharing with the live pages would make
 * the undo baseline drift silently with every change.
 */
const snapshot = (value) => {
  const copy = JSON.parse(JSON.stringify(value));
  const freeze = (v) => {
    if (v && typeof v === 'object' && !Object.isFrozen(v)) {
      Object.values(v).forEach(freeze);
      Object.freeze(v);
    }
    return v;
  };
  return freeze(copy);
};

/** djb2 — a stable id from content, so the same document always yields the same session. */
const hash = (input) => {
  let h = 5381;
  const s = String(input);
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/**
 * Create an editor session from a rendered document.
 *
 * @param {Object} document  native editor JSON from the Rendering Engine.
 * @param {Object} [options]
 * @param {Function} [options.resolveAsset]  (src, element) => src — the CDN seam.
 * @param {string} [options.name]            the design name.
 * @param {string} [options.designId]        an existing design, if any.
 * @param {Object} [options.context]         categoryName / designType / templateTypeId, passed through.
 * @param {boolean} [options.autosave]       defaults to true for a new design.
 * @param {Object} [options.capabilities]    future flags: collaboration, comments, versionHistory.
 * @returns {Object} the session: { ok, session?, errors, warnings, analytics }
 */
export const createEditorSession = (document, options = {}) => {
  const opts = isObj(options) ? options : {};
  const compatibility = validateEditorCompatibility(document);

  if (!compatibility.valid) {
    // A document the editor cannot open never becomes a session.
    return {
      ok: false,
      session: null,
      errors: compatibility.errors,
      warnings: compatibility.warnings,
      analytics: [{ event: ANALYTICS_EVENTS.SESSION_REJECTED, props: { errors: compatibility.errors.length, reason: compatibility.errors[0].message } }],
    };
  }

  const doc = document;
  const metadata = isObj(doc.metadata) ? doc.metadata : {};

  // ── prepare ───────────────────────────────────────────────────────────────
  const normalized = normalizeEditorDocument(doc);
  // Model-controlled URLs are made safe before anything derives from them.
  const safePages = resolveBackgrounds(normalized.pages);
  const theme = resolveTheme(doc, safePages);
  const { pages: withAssets, assets } = resolveAssets(safePages, opts.resolveAsset || identityAssetResolver);
  const pages = applyTheme(withAssets, theme);
  const fonts = resolveFonts(pages);
  const placeholders = resolveImagePlaceholders(pages);

  const sessionId = `s-${hash(JSON.stringify({ pages, canvasSize: normalized.canvasSize }))}`;
  const name = isStr(opts.name) ? opts.name : (isStr(metadata.title) ? metadata.title : EDITOR_DEFAULTS.designName);
  const designId = isStr(opts.designId) ? opts.designId : null;
  const capabilities = CAPABILITIES.reduce((acc, key) => ({
    ...acc,
    [key]: key === 'assetCdn'
      ? typeof opts.resolveAsset === 'function'
      : Boolean(isObj(opts.capabilities) && opts.capabilities[key]),
  }), {});

  // ── the payload the editor opens with ─────────────────────────────────────
  const template = {
    id: designId,
    name,
    template_json: { canvas_width: normalized.canvasSize.width, canvas_height: normalized.canvasSize.height, pages },
  };

  const session = {
    schema: SESSION_SCHEMA_ID,
    version: SESSION_VERSION,
    id: sessionId,

    // Exactly what EditorPage reads. Nothing here needs converting.
    editorState: {
      template,
      pages,
      canvasSize: normalized.canvasSize,
      designName: name,
      ...(isObj(opts.context) ? opts.context : {}),
    },

    // The editor owns undo; this is the immutable ground state it starts from.
    // It is a deep, frozen copy: the baseline must not drift as the document
    // is edited.
    history: { past: [], present: snapshot({ pages, canvasSize: normalized.canvasSize }), future: [], limit: HISTORY_LIMIT },
    undo: { canUndo: false, canRedo: false, depth: 0, limit: HISTORY_LIMIT },
    selection: {
      currentPageId: pages[0].id,
      selectedElementId: null,
      selectedElementIds: [],
      editingGroupId: null,
    },
    autosave: {
      enabled: opts.autosave !== false && designId === null,
      key: designId ? `${AUTOSAVE.keyPrefix}${designId}` : AUTOSAVE.newDesignKey,
      debounceMs: AUTOSAVE.debounceMs,
    },

    // Resolved ahead of time so the editor never waits on us.
    fonts,
    assets,
    placeholders,
    theme,
    variants: Array.isArray(metadata.variants) ? metadata.variants.map((v) => ({ ...v })) : [],

    metadata: {
      ...metadata,
      designId,
      sessionId,
      pageCount: pages.length,
      elementCount: pages.reduce((sum, p) => sum + p.elements.length, 0),
      integration: SESSION_SCHEMA_ID,
      integrationVersion: SESSION_VERSION,
    },

    capabilities,
    // Real seams: the identity a future feature needs, wired to nothing today.
    collaboration: { enabled: capabilities.collaboration, sessionId, documentId: designId, participants: [], presence: [] },
    comments: { enabled: capabilities.comments, threads: [] },
    versionHistory: { enabled: capabilities.versionHistory, versions: [], baseVersion: sessionId },
  };

  return {
    ok: true,
    session,
    errors: [],
    warnings: compatibility.warnings,
    analytics: [{
      event: ANALYTICS_EVENTS.SESSION_CREATED,
      props: {
        sessionId,
        genome: metadata.genome || null,
        source: metadata.renderer || null,
        pages: pages.length,
        elements: session.metadata.elementCount,
        fonts: fonts.length,
        placeholders: placeholders.length,
        variants: session.variants.length,
        warnings: compatibility.warnings.length,
      },
    }],
  };
};

/**
 * The navigation payload for the editor route. Building it is this layer's job;
 * performing the navigation is the caller's.
 *
 * @param {Object} session
 * @returns {Object} the value to pass as the route's state.
 */
export const toEditorNavigationState = (session) => ({ ...session.editorState });

export const __internals = { hash, META_NAMESPACE };
